import React, { useState } from 'react';
import { ChevronDown, ChevronRight, ExternalLink, Package } from 'lucide-react';
import { LocationItemDto } from '../../../../../types/locationOverview';
import { useTranslation } from '../../../../../contexts/I18nProvider';
import { hasExpiryDate, isTrackedSerial } from '../../../../../utils/itemType';
import { formatDate, formatQty } from '../../item-card/format';
import { SectionCard } from '../../item-card/sections/SectionCard';

interface LocationItemsSectionProps {
  items: LocationItemDto[];
  onOpenItem: (itemNo: string) => void;
}

const badge = (text: string, classes: string) =>
  <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${classes}`}>{text}</span>;

/**
 * Varene på lokasjonen — én linje per vare (saldo summert), serie-/batchrader bak en ekspander.
 * Dedikert vare vises også uten saldo («Tom fast plass»). Merker som i varekortets «Hvor er den?»:
 * dedikert (StcBin.ItemNo), fast plukk (Items.StdBinCode), standard (Items.StdLocationName).
 */
export const LocationItemsSection: React.FC<LocationItemsSectionProps> = ({ items, onOpenItem }) => {
  const { t } = useTranslation();
  const prefix = 'warehouse.modules.locationLookup.items';
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const toggle = (itemNo: string) => setExpanded(prev => {
    const next = new Set(prev);
    if (next.has(itemNo)) next.delete(itemNo); else next.add(itemNo);
    return next;
  });

  const stockedCount = items.filter(i => i.quantity !== 0).length;

  return (
    <SectionCard
      icon={<Package className="h-4 w-4" />}
      title={t(`${prefix}.title`, {}, 'Varer på lokasjonen')}
      count={stockedCount}
    >
      {items.length === 0 ? (
        <p className="text-sm text-gray-500 py-2">{t(`${prefix}.empty`, {}, 'Lokasjonen er tom')}</p>
      ) : (
        <ul className="divide-y divide-gray-100 -mx-4 sm:-mx-5">
          {items.map(item => {
            const isOpen = expanded.has(item.itemNo);
            const canExpand = item.rows.length > 0;
            const isSerial = isTrackedSerial(item.itemType) || item.rows.some(r => !!r.serialNo);
            const showExpiry = hasExpiryDate(item.itemType) || item.rows.some(r => !!r.expiryDate);
            const isEmptySlot = item.quantity === 0;

            return (
              <li key={item.itemNo}>
                <div className="flex items-stretch">
                  <button
                    type="button"
                    onClick={() => canExpand ? toggle(item.itemNo) : onOpenItem(item.itemNo)}
                    className="flex-1 min-w-0 flex items-center gap-3 px-4 sm:px-5 py-3 min-h-[56px] text-left hover:bg-gray-50 active:bg-gray-100"
                    aria-expanded={canExpand ? isOpen : undefined}
                  >
                    <span className="flex-shrink-0 w-5 text-gray-400">
                      {canExpand && (isOpen ? <ChevronDown className="h-5 w-5" /> : <ChevronRight className="h-5 w-5" />)}
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-1.5">
                        <span className="text-sm font-medium text-gray-900 font-mono break-words">{item.itemNo}</span>
                        {item.isDedicated && badge(t(`${prefix}.dedicated`, {}, 'Dedikert'), 'bg-purple-100 text-purple-700')}
                        {item.isFixedPick && badge(t(`${prefix}.fixedPick`, {}, 'Fast plukk'), 'bg-blue-100 text-blue-700')}
                        {item.isStandardLocation && !item.isFixedPick && badge(t(`${prefix}.standard`, {}, 'Standard'), 'bg-gray-100 text-gray-700')}
                        {isEmptySlot && badge(t(`${prefix}.emptySlot`, {}, 'Tom fast plass'), 'bg-orange-100 text-orange-700')}
                      </div>
                      <div className="text-sm text-gray-600 break-words">{item.itemDescription || '-'}</div>
                      {canExpand && (
                        <div className="text-xs text-gray-500 mt-0.5">
                          {isSerial
                            ? t(`${prefix}.serialCount`, { '0': item.rows.length }, `${item.rows.length} serienr.`)
                            : t(`${prefix}.batchCount`, { '0': item.rows.length }, `${item.rows.length} batcher`)}
                        </div>
                      )}
                    </div>
                    <div className="flex-shrink-0 text-right">
                      <div className="text-sm font-semibold text-gray-900">{formatQty(item.quantity)}</div>
                      {item.reserved !== 0 && (
                        <div className="text-xs text-orange-600">
                          {t(`${prefix}.reservedShort`, { '0': formatQty(item.reserved) }, `Res. ${formatQty(item.reserved)}`)}
                        </div>
                      )}
                      {!isEmptySlot && (
                        <div className={`text-xs ${item.available < 0 ? 'text-red-600' : 'text-green-600'}`}>
                          {t(`${prefix}.availableShort`, { '0': formatQty(item.available) }, `Tilgj. ${formatQty(item.available)}`)}
                        </div>
                      )}
                    </div>
                  </button>
                  <button
                    type="button"
                    onClick={() => onOpenItem(item.itemNo)}
                    className="flex-shrink-0 px-3 sm:px-4 min-w-[48px] flex items-center justify-center text-blue-600 hover:bg-blue-50 border-l border-gray-100"
                    title={t(`${prefix}.openItemCard`, {}, 'Åpne varekort')}
                    aria-label={t(`${prefix}.openItemCard`, {}, 'Åpne varekort')}
                  >
                    <ExternalLink className="h-4 w-4" />
                  </button>
                </div>

                {canExpand && isOpen && (
                  <div className="bg-gray-50 px-4 sm:px-5 pb-3 pt-1">
                    <table className="w-full text-sm">
                      <thead>
                        <tr>
                          <th className="py-1.5 pl-8 text-left text-xs font-medium text-gray-500 uppercase">
                            {isSerial ? t(`${prefix}.serial`, {}, 'Serienr.') : t(`${prefix}.batch`, {}, 'Batch')}
                          </th>
                          {showExpiry && <th className="py-1.5 text-left text-xs font-medium text-gray-500 uppercase">{t(`${prefix}.expiry`, {}, 'Utløper')}</th>}
                          <th className="py-1.5 text-right text-xs font-medium text-gray-500 uppercase">{t(`${prefix}.quantity`, {}, 'Antall')}</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {item.rows.map((row, i) => (
                          <tr key={`${row.serialNo || ''}-${row.batchNo || ''}-${i}`}>
                            <td className="py-1.5 pl-8 font-mono text-xs break-all">{(isSerial ? row.serialNo : row.batchNo) || row.batchNo || row.serialNo || '-'}</td>
                            {showExpiry && <td className="py-1.5 text-xs">{formatDate(row.expiryDate)}</td>}
                            <td className="py-1.5 text-right text-xs font-semibold">{formatQty(row.quantity)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </SectionCard>
  );
};
